import { Component, Listen, Prop, h, State } from '@stencil/core';
import type { labelProviderInterface } from '../../../../../LabelProviderInterface';
import dynamicBannerStore from '../dynamic-banner-store';
import { getAllByType, answerViewTypes, getByType } from '../../../conversational-search-logic/utils/utils';
import { ConversationalSearchFeedback } from '../../../conversational-search-partials/feedback/feedback';
import { ChatIcons, ChatIconsColors, type ConversationalSearchProductHierarchy, type ConversationalSearchProductSuggestions } from '../../../conversational-search-types/conversational-search-types.type';

@Component({
  tag: 'uiaas-search-widget-conversational-search-dynamic-banner-data-result',
  styleUrl: 'data-result.scss',
  shadow: false,
})
export class WidgetConversationalSearchDynamicBannerDataResult {
  @Prop() readonly query: string;
  @Prop() readonly labelProvider: labelProviderInterface;
  @Prop() readonly data: (ConversationalSearchProductHierarchy & ConversationalSearchProductSuggestions)[];
  @Prop() readonly conversationalSearchMedaliaFormId: string;
  @Prop() readonly conversationalSearchBrandUrl: string;
  @Prop() readonly country: string;
  @Prop() readonly language: string;

  @State() isFeedbackFallback = false;

  private feedbackFallbackTimeout: NodeJS.Timeout;

  @Listen('conversational-search:medalia-fallback-banner', { target: 'window' })
  handleReloadAndSubmitAction() {
      this.isFeedbackFallback = true;
      this.feedbackFallbackTimeout = setTimeout(() => {
          this.isFeedbackFallback = false;
      }, 4000);
  }

  disconnectedCallback() {
      clearTimeout(this.feedbackFallbackTimeout);
  }

  render() {
      const queryToRender = dynamicBannerStore.get('question') ?? this.query;
      const suggestionsData = getAllByType<ConversationalSearchProductSuggestions>(this.data, answerViewTypes.SuggestionResults);
      const hierarchyData = getByType<ConversationalSearchProductHierarchy>(this.data, answerViewTypes.HierarchyResults);
      const outroText = this.labelProvider(
        'conversational.search.banner.dynamic.data.result.outro.disclaimer.v2',
        'This feature is powered by Artificial Intelligence and generates automated responses that may not always be accurate.',
      );

      const tooltipText = this.labelProvider(
        'conversational.search.banner.dynamic.data.result.outro.disclaimer.tooltip',
        'This feature is powered by Artificial Intelligence and generates automated responses that may not always be accurate, complete or updated. Do not rely solely on this information for decision-making. By using it, you acknowledge interacting with AI and agree with our {0} and {1}',
      );

      const termsOfUse = this.labelProvider('conversational.search.banner.dynamic.data.result.outro.disclaimer.tooltip.termsOfUse', 'Terms of Use');
      const privacyPolicy = this.labelProvider('conversational.search.banner.dynamic.data.result.outro.disclaimer.tooltip.privacyPolicy', 'Privacy Policy');
      const tooltipHtmlText = tooltipText.replace('{0}', `<span class='tooltip-links'>${termsOfUse}</span>`).replace('{1}', `<span class='tooltip-links'>${privacyPolicy}</span>`);

      return (
        <section class="conversational-search-banner-dynamic-data-result">
          <uiaas-search-widget-conversational-search-dynamic-banner-result
            labelProvider={(e, f) => this.labelProvider(e, f)}
            hierarchyData={hierarchyData}
            suggestionsData={suggestionsData}
            query={queryToRender}
            conversationalSearchBrandUrl={this.conversationalSearchBrandUrl}
            country={this.country}
            language={this.language}
          />
          <uiaas-search-widget-conversational-search-dynamic-banner-actions
            labelProvider={(e, f) => this.labelProvider(e, f)}
            query={queryToRender}
          />
          <div class="conversational-search-banner-dynamic-data-result-footer">
            {!this.isFeedbackFallback ? <ConversationalSearchFeedback
              text={outroText}
              bannerVariant={true}
              tooltipText={tooltipHtmlText}
            /> :
              <section class="conversational-search-banner-dynamic-data-result-footer-fallback">
                <se-icon color={ChatIconsColors.WARNING_ICON_COLOR}>{ChatIcons.WARNING_ICON}</se-icon>
                <div>
                  <span class="conversational-search-banner-dynamic-data-result-footer-fallback__intro">
                    {this.labelProvider('conversational.search.action.message.medalia.intro', 'Browser\'s pop-up blocker is preventing the modal from appearing.')}
                  </span>
                  <span>
                    {this.labelProvider('conversational.search.action.message.medalia.outro', 'Adjust your browser settings to allow pop-ups from context site.')}
                  </span>
                </div>
              </section>}
          </div>
        </section>
      );
  }
}
